import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserCircle,
  faSignOutAlt,
  faHeart,
  faCreditCard,
  faBell,
  faMapMarkerAlt,
  faHome,
  faSearch,
  faRedo,
  faSpinner,
  faExclamationCircle,
  faChevronDown, // For dropdown arrows
  faStar, // For ratings
  faMoneyBillWave, // For bill payments
  faHouseUser, // For 'My Apartment' if needed
  faAngleRight, // For navigation flow
  faThLarge, // For grid view
  faFilter, // For filter button
} from "@fortawesome/free-solid-svg-icons";
import { AuthGuard } from "../App";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const PropertyMarketPlace = () => {
  const [properties, setProperties] = useState([]);
  const [locations, setLocations] = useState([]);
  const [houseTypes, setHouseTypes] = useState([]);
  const [selectedLocation, setSelectedLocation] = useState("");
  const [selectedHouseType, setSelectedHouseType] = useState("");
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notificationsCount, setNotificationsCount] = useState(0);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]); // To store actual notifications
  const userDataString = localStorage.getItem("user");
  const userData = JSON.parse(userDataString);
  const userName = userData?.fullName || "User";
  const userId = userData.unique;
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  // Fetch initial data (locations, house types, all properties, all rentals, notifications)
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        if (!token) {
          setError("Authentication token missing. Please log in.");
          setLoading(false);
          return;
        }

        const headers = { Authorization: `Bearer ${token}` };

        // Concurrent fetching for efficiency
        const [
          locationResponse,
          houseTypeResponse,
          propertyResponse,
          rentalResponse,
          notificationsResponse, // Fetch notifications
        ] = await Promise.all([
          axios.get("https://backend.inyumba.co.ke/iNyumba/viewlocations", { headers }),
          axios.get("https://backend.inyumba.co.ke/iNyumba/viewhousetype", { headers }),
          axios.get("https://backend.inyumba.co.ke/iNyumba/admin/properties/records", { headers }),
          axios.get("https://backend.inyumba.co.ke/iNyumba/admin/rentals/filterrental", { headers }),
          axios.get("https://backend.inyumba.co.ke/iNyumba/inappnotifications/viewallnotifications", { headers }),
        ]);

        setLocations(locationResponse.data.response);
        setHouseTypes(houseTypeResponse.data.response);
        setProperties(propertyResponse.data.response);
        setRentals(rentalResponse.data.response);
        setNotificationsCount(notificationsResponse.data.response.length);
        setNotifications(notificationsResponse.data.response);

        setLoading(false);
      } catch (err) {
        console.error("Error fetching initial data:", err);
        setError("Failed to load marketplace data. Please try again.");
        setLoading(false);
        toast.error("Failed to load data. Please refresh.");
      }
    };

    fetchData();
  }, [token]); // Dependency on token to re-fetch if it changes

  const handleLocationChange = (event) => {
    setSelectedLocation(event.target.value);
  };

  const handleHouseTypeChange = (event) => {
    setSelectedHouseType(event.target.value);
  };

  const handleResetFilters = () => {
    setSelectedLocation("");
    setSelectedHouseType("");
    handleApplyFilters(true); // Re-fetch all rentals after reset
  };

  const handleFavoritesClick = () => {
    navigate("/user/favorite");
  };

  const handlePropertyClick = (propertyId) => {
    localStorage.setItem("specificPropertyId", propertyId);
    navigate("/user/specificproperty");
  };

  const handleRentalClick = (rentalId) => {
    localStorage.setItem("specificRentalId", rentalId);
    navigate("/user/specificrental");
  };

  const handleBillPaymentsClick = () => {
    navigate("/user/bills");
  };

  const handleProfileClick = () => {
    navigate("/user/profile");
  };

  const handleLogoutClick = () => {
    navigate("/logout");
  };

  const toggleNotifications = () => {
    setIsNotificationsOpen(!isNotificationsOpen);
  };

  const handleApplyFilters = async (isReset = false) => {
    setLoading(true);
    setError(null);
    try {
      const queryParams = {};
      if (selectedLocation && !isReset) {
        queryParams.location = selectedLocation;
      }
      if (selectedHouseType && !isReset) {
        queryParams.house_type = selectedHouseType;
      }

      const response = await axios.get(
        "https://backend.inyumba.co.ke/iNyumba/admin/rentals/filterrental",
        {
          headers: { Authorization: `Bearer ${token}` },
          params: queryParams,
        }
      );
      setRentals(response.data.response);
      setLoading(false);
      toast.success("Filters applied successfully!");
    } catch (err) {
      console.error("Error fetching filtered rentals:", err);
      setError("Failed to apply filters. Please try again.");
      setLoading(false);
      toast.error("Failed to apply filters.");
    }
  };

  if (loading) {
    return (
      <AuthGuard>
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
          <div className="flex flex-col items-center text-gray-600">
            <FontAwesomeIcon icon={faSpinner} spin className="text-5xl mb-4 text-indigo-500" />
            <p className="text-xl font-medium">Loading marketplace...</p>
          </div>
        </div>
      </AuthGuard>
    );
  }

  if (error) {
    return (
      <AuthGuard>
        <div className="min-h-screen flex items-center justify-center bg-red-50">
          <div className="flex flex-col items-center text-red-700 p-8 rounded-lg bg-white shadow-lg border border-red-200">
            <FontAwesomeIcon icon={faExclamationCircle} className="text-5xl mb-4" />
            <h2 className="text-2xl font-bold mb-2">Error</h2>
            <p className="text-center">{error}</p>
            <p className="mt-4 text-sm text-gray-500">Please refresh the page or contact support.</p>
          </div>
        </div>
      </AuthGuard>
    );
  }

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100 flex flex-col">
       
        {/* Sticky Header */}
        <header className="sticky top-0 z-50 w-full bg-white bg-opacity-90 backdrop-blur-md shadow-lg py-4 px-6 flex items-center justify-between animate-fade-in-down border-b border-gray-100">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold text-gray-800 hidden sm:block">iNyumba Marketplace</h1>
          </div>

          <div className="flex items-center space-x-4">
            {/* Profile Icon */}
            <div
              className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-purple-200 to-indigo-200 text-purple-700 cursor-pointer hover:from-purple-300 hover:to-indigo-300 transition-all duration-300 transform hover:scale-105 shadow-md"
              onClick={handleProfileClick}
              aria-label="User Profile"
            >
              <FontAwesomeIcon icon={faUserCircle} className="text-xl" />
            </div>

            {/* Notifications */}
            <div className="relative">
              <div
                className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-blue-200 to-cyan-200 text-blue-700 cursor-pointer hover:from-blue-300 hover:to-cyan-300 transition-all duration-300 transform hover:scale-105 shadow-md"
                onClick={toggleNotifications}
                aria-label="View Notifications"
              >
                <FontAwesomeIcon icon={faBell} className="text-xl" />
              </div>
              {notificationsCount > 0 && (
                <span className="absolute -top-1 -right-1 text-xs font-bold bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center ring-2 ring-white animate-pulse">
                  {notificationsCount}
                </span>
              )}
              {isNotificationsOpen && (
                <div className="absolute top-12 right-0 w-72 bg-white text-gray-800 p-4 rounded-xl shadow-2xl border border-gray-100 z-50 max-h-72 overflow-y-auto transform origin-top-right animate-scale-in">
                  <h3 className="font-extrabold text-lg mb-3 pb-2 border-b-2 border-indigo-200 text-indigo-700">Notifications</h3>
                  {notifications.length > 0 ? (
                    notifications.map((notification) => (
                      <div key={notification.notificationid} className="mb-2 p-3 bg-gray-50 rounded-lg text-sm transition-colors duration-200 hover:bg-gray-100">
                        {notification.notification}
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-4">No new notifications.</p>
                  )}
                </div>
              )}
            </div>

            {/* Logout Icon */}
            <div
              className="flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-br from-red-200 to-pink-200 text-red-700 cursor-pointer hover:from-red-300 hover:to-pink-300 transition-all duration-300 transform hover:scale-105 shadow-md"
              onClick={handleLogoutClick}
              aria-label="Log Out"
            >
              <FontAwesomeIcon icon={faSignOutAlt} className="text-xl" />
            </div>
          </div>
        </header>

        <main className="flex-grow p-6 sm:p-8 lg:p-10">
          {/* Top Feature Cards */}
          <section className="mb-10 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-4xl mx-auto animate-fade-in-up">
            <div
              className="flex items-center justify-center p-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl shadow-lg cursor-pointer hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105"
              onClick={handleBillPaymentsClick}
            >
              <FontAwesomeIcon icon={faCreditCard} className="text-3xl mr-4" />
              <span className="text-xl font-semibold">Bill Payments</span>
            </div>
            <div
              className="flex items-center justify-center p-6 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-xl shadow-lg cursor-pointer hover:from-pink-600 hover:to-rose-700 transition-all duration-300 transform hover:scale-105"
              onClick={handleFavoritesClick}
            >
              <FontAwesomeIcon icon={faHeart} className="text-3xl mr-4" />
              <span className="text-xl font-semibold">My Favorites</span>
            </div>
          </section>

          {/* Section Heading: Properties */}
          <section className="mb-8 animate-fade-in">
            <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-4">
              Explore Properties
            </h2>
            <p className="text-lg text-gray-700 text-center max-w-2xl mx-auto">
              Discover buildings and estates where various rental units are available.
            </p>
          </section>

          {/* Properties Scrollable Container */}
          <section className="flex overflow-x-auto space-x-6 p-4 -mx-4 pb-6 scrollbar-hide mb-12 lg:px-0">
            {properties.length > 0 ? (
              properties.map((property) => (
                <div
                  key={property.id}
                  className="flex-shrink-0 w-72 bg-white shadow-xl rounded-xl overflow-hidden cursor-pointer
                             hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-102 group animate-fade-in-up"
                  onClick={() => handlePropertyClick(property.property_id)}
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={property.photo}
                      alt={property.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                      <h3 className="text-lg font-semibold text-white">{property.title}</h3>
                    </div>
                  </div>
                  <div className="p-4">
                    <p className="text-sm text-gray-700 mb-2 line-clamp-2">{property.description}</p>
                    <div className="flex justify-between items-center text-sm text-gray-500">
                      <span><FontAwesomeIcon icon={faMapMarkerAlt} className="mr-1 text-indigo-500" /> {property.location}, {property.county}</span>
                      {/* You can add property ratings here if available */}
                      {/* <span className="text-yellow-500">
                                            <FontAwesomeIcon icon={faStar} /> {propertyRatings[property.id] || 'N/A'}
                                        </span> */}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-600 w-full">No properties available at the moment.</p>
            )}
          </section>

          {/* Section Heading: Rentals */}
          <section className="mb-8 animate-fade-in">
            <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-4">
              Available Rental Units
            </h2>
            <p className="text-lg text-gray-700 text-center max-w-2xl mx-auto">
              Browse individual apartments, houses, and rooms ready for rent.
            </p>
          </section>

          {/* Filter Section */}
          <section className="bg-white p-6 rounded-2xl shadow-xl mb-12 max-w-4xl mx-auto animate-fade-in-up">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Find Your Perfect Rental</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
              {/* Location Dropdown */}
              <div className="relative">
                <select
                  value={selectedLocation}
                  onChange={handleLocationChange}
                  className="block w-full p-3 pr-10 border border-gray-300 rounded-lg appearance-none text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition-all duration-200"
                >
                  <option value="">Select Location</option>
                  {locations.map((location, index) => (
                    <option key={index} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
                  <FontAwesomeIcon icon={faChevronDown} />
                </div>
              </div>

              {/* House Type Dropdown */}
              <div className="relative">
                <select
                  value={selectedHouseType}
                  onChange={handleHouseTypeChange}
                  className="block w-full p-3 pr-10 border border-gray-300 rounded-lg appearance-none text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition-all duration-200"
                >
                  <option value="">Select House Type</option>
                  {houseTypes.map((houseType, index) => (
                    <option key={index} value={houseType}>
                      {houseType}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
                  <FontAwesomeIcon icon={faChevronDown} />
                </div>
              </div>

              {/* Buttons Section */}
              <div className="flex space-x-4 justify-center md:justify-end">
                <button
                  onClick={handleResetFilters}
                  className="flex items-center bg-gray-200 text-gray-700 px-5 py-2 rounded-lg font-semibold hover:bg-gray-300 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <FontAwesomeIcon icon={faRedo} className="mr-2" /> Reset
                </button>
                <button
                  onClick={() => handleApplyFilters(false)}
                  className="flex items-center bg-indigo-600 text-white px-5 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  <FontAwesomeIcon icon={faSearch} className="mr-2" /> Apply Filters
                </button>
              </div>
            </div>
          </section>

          {/* Rentals Grid */}
          <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 px-4 pb-10">
            {rentals.length > 0 ? (
              rentals.map((rental) => (
                <div
                  key={rental.rental_id}
                  className="bg-white shadow-xl rounded-xl overflow-hidden cursor-pointer
                             hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-102 group animate-fade-in-up"
                  onClick={() => handleRentalClick(rental.rental_id)}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={rental.photo}
                      alt={rental.property_name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    />
                    <div className="absolute top-2 right-2 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md">
                      {rental.house_type}
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1 line-clamp-1">{rental.property_name}</h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{rental.description}</p>

                    <div className="text-sm text-gray-700 mb-3">
                      <p className="flex items-center mb-1">
                        <FontAwesomeIcon icon={faMapMarkerAlt} className="mr-2 text-indigo-500" />
                        <span className="font-semibold">Location:</span> {rental.location}
                      </p>
                    </div>

                    <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                      <p className="text-xl font-bold text-green-600">
                        KES {Number(rental.rent_price).toLocaleString()}
                      </p>
                      {/* Optional: Rental Rating (if available in your data) */}
                      {/* <span className="text-yellow-500">
                                                <FontAwesomeIcon icon={faStar} /> {rentalRatings[rental.rental_id] || 'N/A'}
                                            </span> */}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-600 col-span-full py-10">No rental units match your criteria.</p>
            )}
          </section>
        </main>
      </div>
    </AuthGuard>
  );
};

export default PropertyMarketPlace;